import React from 'react';
import { Box, Grid, Paper, Typography, Chip, Skeleton, useTheme, alpha } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailableRounded';
import EventBusyIcon from '@mui/icons-material/EventBusyRounded';
import InsightsIcon from '@mui/icons-material/InsightsRounded';
import SchoolIcon from '@mui/icons-material/SchoolRounded';
import { motion } from 'framer-motion';
import { getAttendanceDashboardCount, getStudentUser } from '../api/studentApi';
import StatCard from '../components/StatCard';
import { tokens } from '../theme/tokens';
import { staggerContainer, fadeInUp } from '../utils/motion';

const MotionPaper = motion(Paper);
const MotionChip = motion(Chip);

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 5) return 'Still up studying?';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Burning the midnight oil?';
};

const Dashboard = () => {
  const theme = useTheme();
  const user = getStudentUser();
  const [attendance, setAttendance] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await getAttendanceDashboardCount();
        setAttendance(res.data);
      } catch (err) {
        console.error('Failed to load attendance dashboard', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const firstName = (user?.admission_details?.student_name || user?.employee_name || 'Student').split(' ')[0];

  return (
    <Box component={motion.div} variants={staggerContainer} initial="hidden" animate="show">
      {/* Hero: the one deliberate motion moment on this page -- a slow-drifting
          gradient mesh behind a personalised, time-aware greeting. */}
      <MotionPaper
        variants={fadeInUp}
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '22px',
          p: { xs: 3, sm: 4 },
          mb: 3,
          color: '#fff',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${tokens.violetDeep} 100%)`,
        }}
      >
        <Box
          sx={{
            position: 'absolute', width: 260, height: 260, borderRadius: '50%',
            background: alpha('#fff', 0.12), top: -110, right: -60,
            animation: 'floatSlow 9s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute', width: 160, height: 160, borderRadius: '50%',
            background: alpha(tokens.amber, 0.25), bottom: -70, right: 120,
            animation: 'floatSlow 7s ease-in-out infinite 1s',
          }}
        />
        <Box sx={{ position: 'relative' }}>
          <Typography variant="body2" sx={{ opacity: 0.85, mb: 0.5, fontWeight: 600 }}>
            {getGreeting()}
          </Typography>
          <Typography variant="h4" sx={{ fontFamily: 'var(--font-display)', fontWeight: 800, mb: 1 }}>
            {firstName} 👋
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, maxWidth: 420 }}>
            {user?.admission_details?.admission_no ? `Admission No. ${user.admission_details.admission_no}` : 'Welcome back to your learning space.'}
            {user?.branch_name ? ` — ${user.branch_name}` : ''}
          </Typography>
        </Box>
      </MotionPaper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {loading ? (
          [0, 1, 2].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rounded" height={92} sx={{ borderRadius: '18px' }} />
            </Grid>
          ))
        ) : (
          <>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<EventAvailableIcon />}
                label="Present days"
                value={attendance?.present_count ?? attendance?.present ?? '—'}
                color={tokens.mint}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<EventBusyIcon />}
                label="Absent days"
                value={attendance?.absent_count ?? attendance?.absent ?? '—'}
                color={tokens.coral}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<InsightsIcon />}
                label="Attendance"
                value={attendance?.percentage ?? '—'}
                suffix={attendance?.percentage != null ? '%' : ''}
                color={theme.palette.primary.main}
              />
            </Grid>
          </>
        )}
      </Grid>

      <MotionPaper variants={fadeInUp} elevation={0} sx={{ p: 3, borderRadius: '18px', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon fontSize="small" color="primary" /> Enrolled courses
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(user?.courses || []).map((c, i) => (
            <MotionChip
              key={c.course_id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.05 * i, duration: 0.3 }}
              label={`${c.course_name} — ${c.batch_name}`}
              sx={{
                px: 0.5, py: 2.2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            />
          ))}
          {(!user?.courses || user.courses.length === 0) && (
            <Typography variant="body2" color="text.secondary">No courses found.</Typography>
          )}
        </Box>
      </MotionPaper>
    </Box>
  );
};

export default Dashboard;
